import CurrentProgramSelector from "@/components/programs/CurrentProgramSelector";
import {
  COMMAND_CENTER,
  COMMAND_CENTER_ACCOUNT_MANAGEMENT,
  COMMAND_CENTER_AUTHORIZATION,
  COMMAND_CENTER_SUPPORT,
  COMMAND_CENTER_VENDOR_MANAGEMENT,
  COMPLIANCE_MATCHES,
  COMPLIANCE_OVERVIEW,
  COMPLIANCE_PROGRAMS,
  COMPLIANCE_SUBMISSIONS,
  COMPLIANCE_TASKS,
  ENERGY_MANAGEMENT_INITIATIVES,
  ENERGY_MANAGEMENT_INSIGHTS,
  ENERGY_MANAGEMENT_LOCATIONS,
  ENERGY_MANAGEMENT_METERS,
  ENERGY_MANAGEMENT_STATEMENTS,
  ENERGY_MANAGEMENT_UTILITIES,
  JOBS,
  NOTIFICATIONS,
  PROGRAMS_COVERAGE_LISTS,
  PROGRAMS_OVERVIEW,
  PROGRAMS_PROGRAMS,
  programUrl,
  SETTINGS,
  SUPPORT,
} from "@/routing";
import { ProgramResponse } from "@/viaduct-api";
import AccountCircleOutlined from "@mui/icons-material/AccountCircleOutlined";
import DashboardOutlined from "@mui/icons-material/DashboardOutlined";
import ElectricMeterOutlined from "@mui/icons-material/ElectricMeterOutlined";
import FactoryOutlined from "@mui/icons-material/FactoryOutlined";
import FastForwardOutlined from "@mui/icons-material/FastForwardOutlined";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import LocationCityOutlined from "@mui/icons-material/LocationCityOutlined";
import ListAltOutlined from "@mui/icons-material/ListAltOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import NotificationsOutlined from "@mui/icons-material/NotificationsOutlined";
import PlaylistAddCheckOutlined from "@mui/icons-material/PlaylistAddCheckOutlined";
import PublishOutlined from "@mui/icons-material/PublishOutlined";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import ReceiptOutlined from "@mui/icons-material/ReceiptOutlined";
import SettingsApplicationsOutlined from "@mui/icons-material/SettingsApplicationsOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import SupportOutlined from "@mui/icons-material/SupportOutlined";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import Divider from "@mui/joy/Divider";
import { ReactNode } from "react";

export interface AppRoute {
  text: string;
  icon?: ReactNode;
  href?: string;
  children?: AppRoute[];
  defaultExpanded?: boolean;
  /** Disabled link — rendered greyed out, not clickable. */
  disabled?: boolean;
  /** Optional tooltip / helper text shown when hovering the row. */
  hint?: string;
  /** Replace the entire menu item with this node (used for the
   *  current-program selector at the top of the Programs section). */
  render?: ReactNode;
}

export const EnergyManagementRoutes: AppRoute[] = [
  {
    text: "Utilities",
    icon: <FactoryOutlined />,
    href: ENERGY_MANAGEMENT_UTILITIES,
  },
  {
    text: "Locations",
    icon: <LocationCityOutlined />,
    href: ENERGY_MANAGEMENT_LOCATIONS,
  },
  {
    text: "Meters",
    icon: <ElectricMeterOutlined />,
    href: ENERGY_MANAGEMENT_METERS,
  },
  {
    text: "Statements",
    icon: <ReceiptOutlined />,
    href: ENERGY_MANAGEMENT_STATEMENTS,
  },
  {
    text: "Initiatives",
    icon: <FastForwardOutlined />,
    href: ENERGY_MANAGEMENT_INITIATIVES,
  },
  {
    text: "Insights",
    icon: <LightbulbOutlined />,
    href: ENERGY_MANAGEMENT_INSIGHTS,
  },
  {
    icon: <UploadFileOutlined />,
    text: "Uploads",
    href: JOBS,
  },
];

export const PerformanceComplianceRoutes: AppRoute[] = [
  {
    text: "Overview",
    icon: <DashboardOutlined />,
    href: COMPLIANCE_OVERVIEW,
  },
  {
    text: "Programs",
    icon: <ReceiptLongOutlined />,
    href: COMPLIANCE_PROGRAMS,
  },
  {
    text: "Matches",
    icon: <PlaylistAddCheckOutlined />,
    href: COMPLIANCE_MATCHES,
  },
  {
    text: "Tasks",
    icon: <FastForwardOutlined />,
    href: COMPLIANCE_TASKS,
  },
  {
    text: "Submissions",
    icon: <PublishOutlined />,
    href: COMPLIANCE_SUBMISSIONS,
  },
  // {
  //   text: "Export",
  //   icon: <CloudDownloadOutlined />,
  //   href: COMPLIANCE_EXPORT,
  // },
];

/** Build the Building Performance Programs sidebar routes. The Properties /
 *  Property Sharing / Forms / Communications links are program-scoped: they
 *  point at the current program when one is selected, and are disabled
 *  otherwise. The current-program selector itself is rendered as a custom
 *  node at the top of the section. */
export const buildBuildingPerformanceProgramsRoutes = (
  currentProgram: ProgramResponse | null,
): AppRoute[] => {
  const id = currentProgram?.id;
  const programScopedHint = id
    ? undefined
    : "Select a program above to enable this link.";

  return [
    {
      text: "Overview",
      icon: <DashboardOutlined />,
      href: PROGRAMS_OVERVIEW,
    },
    {
      text: "Programs",
      icon: <ReceiptLongOutlined />,
      href: PROGRAMS_PROGRAMS,
    },
    {
      text: "Coverage Lists",
      icon: <ListAltOutlined />,
      href: PROGRAMS_COVERAGE_LISTS,
    },
    {
      text: "__divider__",
      render: <Divider />,
    },
    {
      text: "__current_program_selector__",
      render: <CurrentProgramSelector />,
    },
    {
      text: "Properties",
      icon: <LocationCityOutlined />,
      href: id ? `${programUrl(id)}/properties` : undefined,
      disabled: !id,
      hint: programScopedHint,
    },
    {
      text: "Property Sharing",
      icon: <LocationCityOutlined />,
      href: id ? `${programUrl(id)}/property-share-requests` : undefined,
      disabled: !id,
      hint: programScopedHint,
    },
    {
      text: "Communications",
      icon: <NotificationsOutlined />,
      href: id ? `${programUrl(id)}/communications` : undefined,
      disabled: !id,
      hint: programScopedHint,
    },
  ];
};

export const getSecondaryRoutes = (userIsSuper?: boolean) => {
  const secondaryRoutes: AppRoute[] = [
    {
      icon: <NotificationsOutlined />,
      text: "Notifications",
      href: NOTIFICATIONS,
    },
    {
      icon: <SupportOutlined />,
      text: "Support",
      href: SUPPORT,
    },
    {
      icon: <SettingsOutlined />,
      text: "Settings",
      href: SETTINGS,
    },
  ];

  if (userIsSuper) {
    secondaryRoutes.push({
      icon: <SettingsApplicationsOutlined />,
      text: "Command Center",
      children: [
        {
          text: "Dashboard",
          icon: <DashboardOutlined />,
          href: COMMAND_CENTER,
        },
        {
          text: "Authorization",
          icon: <LockOutlined />,
          href: COMMAND_CENTER_AUTHORIZATION,
        },
        {
          text: "Account Management",
          icon: <AccountCircleOutlined />,
          href: COMMAND_CENTER_ACCOUNT_MANAGEMENT,
        },
        {
          text: "Vendor Management",
          icon: <FactoryOutlined />,
          href: COMMAND_CENTER_VENDOR_MANAGEMENT,
        },
        {
          text: "Support Management",
          icon: <SupportOutlined />,
          href: COMMAND_CENTER_SUPPORT,
        },
      ],
    });
  }

  return secondaryRoutes;
};
